/**
 * Pagination Type Definitions
 *
 * This file contains types for paginated API responses and pagination
 * request parameters used across the application.
 *
 * The paginated response shape matches the Laravel resource collection
 * payload exactly: `{ data, links, meta }`. This file is the single source
 * of truth for all pagination types.
 *
 * @module types/common/pagination
 */

// =============================================================================
// PAGINATION LINKS & META (Laravel resource collection shape)
// =============================================================================

/**
 * Top-level pagination links (Laravel resource collection).
 *
 * @example
 * ```typescript
 * const links: PaginationLinks = {
 *   first: 'https://api.example.com/resource?page=1',
 *   last: 'https://api.example.com/resource?page=5',
 *   prev: null,
 *   next: 'https://api.example.com/resource?page=2',
 * };
 * ```
 */
export interface PaginationLinks {
  /** URL of the first page (null when not applicable) */
  first: string | null;
  /** URL of the last page (null when not applicable) */
  last: string | null;
  /** URL of the previous page (null on the first page) */
  prev: string | null;
  /** URL of the next page (null on the last page) */
  next: string | null;
}

/**
 * A single rendered page link in `meta.links` (Laravel paginator).
 *
 * @example
 * ```typescript
 * const link: PaginationLink = {
 *   url: 'https://api.example.com/resource?page=2',
 *   label: '2',
 *   active: false,
 * };
 * ```
 */
export interface PaginationLink {
  /** Target URL for the link (null for disabled/ellipsis links) */
  url: string | null;
  /** Rendered label (e.g. '1', '&laquo; Previous') */
  label: string;
  /** Whether this link represents the current page */
  active: boolean;
}

/**
 * Pagination metadata block returned under `meta`.
 *
 * @example
 * ```typescript
 * const meta: PaginationMeta = {
 *   current_page: 1,
 *   from: 1,
 *   last_page: 5,
 *   links: [],
 *   path: 'https://api.example.com/resource',
 *   per_page: 15,
 *   to: 15,
 *   total: 73,
 * };
 * ```
 */
export interface PaginationMeta {
  /** Current page number (1-indexed) */
  current_page: number;
  /** Index of the first item on the current page (null when empty) */
  from: number | null;
  /** Number of the last page */
  last_page: number;
  /** Rendered page links (Previous, page numbers, Next) */
  links: PaginationLink[];
  /** Base path of the paginated resource */
  path: string;
  /** Number of items per page */
  per_page: number;
  /** Index of the last item on the current page (null when empty) */
  to: number | null;
  /** Total number of items across all pages */
  total: number;
}

// =============================================================================
// PAGINATED RESPONSE
// =============================================================================

/**
 * Laravel-style paginated response wrapper.
 * Standard structure for all paginated API responses.
 *
 * @typeParam T - The type of items in the data array
 *
 * @example
 * ```typescript
 * interface Workout { id: string; name: string; }
 *
 * const response: PaginatedResponse<Workout> = {
 *   data: [{ id: '1', name: 'Morning Run' }],
 *   links: { first: '...', last: '...', prev: null, next: '...' },
 *   meta: {
 *     current_page: 1, from: 1, last_page: 5, links: [],
 *     path: '...', per_page: 10, to: 10, total: 50,
 *   },
 * };
 * ```
 */
export interface PaginatedResponse<T = unknown> {
  /** Array of items for the current page */
  data: T[];
  /** Top-level navigation links */
  links: PaginationLinks;
  /** Pagination metadata */
  meta: PaginationMeta;
}

// =============================================================================
// PAGINATION REQUEST PARAMETERS
// =============================================================================

/**
 * Pagination parameters for API requests.
 * Used when fetching paginated data from the server.
 *
 * @example
 * ```typescript
 * const params: PaginationParams = {
 *   page: 2,
 *   per_page: 20,
 *   sort: 'created_at',
 *   order: 'desc',
 * };
 * ```
 */
export interface PaginationParams {
  /** Page number to fetch (1-indexed) */
  page?: number;
  /** Number of items per page */
  per_page?: number;
  /** Field to sort by */
  sort?: string;
  /** Sort order */
  order?: 'asc' | 'desc';
}

/**
 * Search parameters extending pagination.
 * Used for search endpoints with additional filtering.
 *
 * @example
 * ```typescript
 * const params: SearchParams = {
 *   q: 'chest workout',
 *   page: 1,
 *   per_page: 10,
 *   filters: { difficulty: 'intermediate' },
 * };
 * ```
 */
export interface SearchParams extends PaginationParams {
  /** Search query string */
  q?: string;
  /** Additional filters */
  filters?: Record<string, unknown>;
}

/**
 * Date range parameters for filtering by time period.
 * Often used in combination with pagination for reports and analytics.
 *
 * @example
 * ```typescript
 * const dateRange: DateRangeParams = {
 *   start_date: '2024-01-01',
 *   end_date: '2024-01-31',
 *   period: 'month',
 * };
 * ```
 */
export interface DateRangeParams {
  /** Start date in ISO format (YYYY-MM-DD) */
  start_date?: string;
  /** End date in ISO format (YYYY-MM-DD) */
  end_date?: string;
  /** Preset time period */
  period?: 'week' | 'month' | 'quarter' | 'year';
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Check if a response is a paginated response.
 *
 * Validates the Laravel `{ data, links, meta }` shape: `data` must be an
 * array, `links` must be an object, and `meta` must be an object carrying
 * numeric `current_page` and `last_page` fields.
 *
 * @param response - The response to check
 * @returns True if the response matches PaginatedResponse structure
 *
 * @example
 * ```typescript
 * if (isPaginatedResponse(response)) {
 *   console.log(`Page ${response.meta.current_page} of ${response.meta.last_page}`);
 * }
 * ```
 */
export function isPaginatedResponse<T>(
  response: unknown
): response is PaginatedResponse<T> {
  if (response === null || typeof response !== 'object') {
    return false;
  }

  const obj = response as Record<string, unknown>;

  // Check for data array
  if (!('data' in obj) || !Array.isArray(obj.data)) {
    return false;
  }

  // Check for links object
  if (
    !('links' in obj) ||
    typeof obj.links !== 'object' ||
    obj.links === null
  ) {
    return false;
  }

  // Check for meta object
  if (!('meta' in obj) || typeof obj.meta !== 'object' || obj.meta === null) {
    return false;
  }

  const meta = obj.meta as Record<string, unknown>;

  // Verify required meta fields
  return (
    typeof meta.current_page === 'number' && typeof meta.last_page === 'number'
  );
}

/**
 * Check if pagination metadata indicates there are more pages.
 *
 * @param meta - The pagination metadata to check
 * @returns True if there are more pages after the current one
 */
export function hasMorePages(meta: PaginationMeta): boolean {
  return meta.current_page < meta.last_page;
}

/**
 * Check if pagination metadata indicates it's the first page.
 *
 * @param meta - The pagination metadata to check
 * @returns True if this is the first page
 */
export function isFirstPage(meta: PaginationMeta): boolean {
  return meta.current_page <= 1;
}

/**
 * Check if pagination metadata indicates it's the last page.
 *
 * @param meta - The pagination metadata to check
 * @returns True if this is the last page
 */
export function isLastPage(meta: PaginationMeta): boolean {
  return meta.current_page >= meta.last_page;
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Extract the data type from a PaginatedResponse.
 *
 * @typeParam T - The PaginatedResponse type
 *
 * @example
 * ```typescript
 * type WorkoutResponse = PaginatedResponse<Workout>;
 * type WorkoutData = PaginatedResponseData<WorkoutResponse>; // Workout
 * ```
 */
export type PaginatedResponseData<T> =
  T extends PaginatedResponse<infer U> ? U : never;

/**
 * Default pagination parameters.
 * Use as fallback when parameters are not provided.
 */
export const DEFAULT_PAGINATION_PARAMS: Required<
  Pick<PaginationParams, 'page' | 'per_page'>
> = {
  page: 1,
  per_page: 10,
} as const;

/**
 * Create default (empty) pagination metadata.
 * Useful for initial state in stores.
 *
 * @returns Empty pagination metadata
 */
export function createEmptyPaginationMeta(): PaginationMeta {
  return {
    current_page: 1,
    from: null,
    last_page: 1,
    links: [],
    path: '',
    per_page: 0,
    to: null,
    total: 0,
  };
}
